import { ApprovalsService } from 'api-clients/monolith';

import {
  ALWAYS,
  ConditionDraft,
  ConditionLogic,
  conditionSetErrors,
  defaultConditionType,
  draftConditions,
  emptyConditionOfType,
  FieldCatalog,
  isScopingCondition,
  mintConditionKey,
  passthroughConditions,
  scopePassthroughConditions,
  StoredCondition,
  toStoredConditions,
} from './conditionForm';

import {
  ApprovalRule,
  ApprovalRuleApprover,
  ApprovalRuleLevel,
  SubjectType,
} from './types';

/**
 * The write shape, taken from the generated client rather than described again
 * here — so a contract change surfaces as a type error in `toPayload` instead of
 * as a 422 at runtime.
 */
export type RulePayload = Parameters<
  typeof ApprovalsService.postInternalApiApprovalsRules
>[0];

/** One level's approver rows, as the write endpoint expects them. */
type ApproverRows = NonNullable<
  NonNullable<RulePayload['rule']['levels']>[number]['approvers']
>;

/**
 * The escalation units the picker offers (AXHE-3932, Design Requirements §3).
 *
 * Two, not the three `Approvals::RuleLevel::ESCALATE_UNITS` enumerizes:
 * `business_days` is supported by the engine and deliberately not offered here
 * — Michaela's call on AXHE-3974 #5 — and the published contract's enum is these
 * two in both directions. A stored value outside this set is still *preserved*;
 * see `escalateUnitOptions`.
 */
export const ESCALATE_UNITS = ['hours', 'days'] as const;

export type EscalateUnit = (typeof ESCALATE_UNITS)[number];

/** What a level's timer counts in when the admin has not said. */
export const DEFAULT_ESCALATE_UNIT: EscalateUnit = 'days';

/** The number a freshly opened block starts on, as the mock opens it. */
export const DEFAULT_ESCALATE_AFTER = '2';

/**
 * The bounds the timer input carries.
 *
 * The minimum is enforced on save: `RuleLevel` validates
 * `numericality: { greater_than: 0 }`, so a zero or a blank is a row the server
 * refuses anyway.
 *
 * The maximum is **not**. AXHE-3932 states the range as "1–30" and it is the
 * only source that gives one — §3 gives none and the mock's input carries `min`
 * alone — while the OpenAPI document types `escalate_after` as a bare integer
 * with no maximum. So 30 guides the stepper without being a refusal: a rule
 * whose timer the API set to 45 has to stay editable here, and making it
 * unsavable would be this panel inventing a constraint the server does not have.
 */
export const ESCALATE_AFTER_MIN = 1;
export const ESCALATE_AFTER_MAX = 30;

/**
 * The largest wait the column can actually hold.
 *
 * `approval_rule_levels.escalate_after` is a Postgres `integer`, and nothing on
 * either side of the wire bounds it: `RuleLevel` validates only
 * `greater_than: 0`, and `SaveRule` rescues `ActiveRecord::RecordInvalid` alone —
 * so an out-of-range value raises during attribute casting instead of failing
 * validation, and the save 500s rather than returning the 422 the panel could
 * explain. A `type="number"` input's `max` is a hint and does not stop ten digits
 * being typed, so this is the bound that keeps that unreachable.
 *
 * Distinct from `ESCALATE_AFTER_MAX`, which is the range the design states and
 * this one is not: the point of leaving 30 unenforced is that a larger wait the
 * API stored stays editable, and 2147483647 is the largest such wait there is.
 */
const ESCALATE_AFTER_STORABLE_MAX = 2_147_483_647;

/**
 * A level's backup approvers and the timer that brings them in (AXHE-3932).
 *
 * `null` on the level rather than a set of nullable fields: "this level has no
 * escalation" is one state, and the design says so with one control — the block
 * is added and removed whole (§3, "a way to remove the whole block").
 */
export interface BackupDraft {
  /**
   * The named users who take over once the timer expires.
   *
   * The same flat list the primaries are held in, because the primaries are
   * still a flat list. Design Requirements §3 asks for "the same
   * Name/User-attribute row structure" on both, and AXHE-3899 owns replacing
   * both with it — building the row model on the backups alone would put two
   * different approver controls on one card, which is the one shape §3 rules out.
   */
  approverIds: string[];
  /**
   * Held as typed rather than as a number, so an emptied field is a state
   * validation can name instead of a silent zero. Same reason the condition
   * builder holds its amounts as strings.
   */
  escalateAfter: string;
  /**
   * Widened from `EscalateUnit` on purpose: this is the stored token, which may
   * be one the picker does not offer. Narrowed at the payload boundary.
   */
  escalateUnit: string;
  /**
   * The unit the level arrived with, kept beside the edited one.
   *
   * `escalateUnitOptions` reads this rather than `escalateUnit`, so a unit the
   * picker does not offer stays in the list after the admin moves off it. Read
   * from the live value, choosing Days on a `business_days` level recomputed the
   * list to the two design units and took the third option away with no route
   * back short of closing the panel unsaved — a one-way door inside the session,
   * in the one place the point was round-trippability. Same reason a level keeps
   * `storedConditions` beside its condition drafts.
   */
  storedUnit: string;
  /**
   * Role backups this panel cannot render, carried untouched — the same
   * treatment `passthrough.roleApprovers` gives a role *primary*.
   */
  roleApprovers: ApproverRows;
}

/**
 * Whether one approval settles a level or every approver has to give one — the
 * Any/All choice on the level card (AXHE-3899).
 */
export type ApproverLogic = NonNullable<ApprovalRuleLevel['approver_logic']>;

/**
 * The rule as the panel edits it.
 *
 * Deliberately not the API shape. Each level's conditions are held twice — as
 * drafts the builder edits and as the rows the API returned — and every field
 * the panel does *not* own is carried on `passthrough`, so a save re-emits it
 * rather than clearing it. See `toPayload` for why that matters.
 */
export interface LevelDraft {
  /** Stable within one editing session so React keys survive a reorder. */
  key: string;
  name: string;
  /**
   * Carried, never edited (AXHE-4470): the locked design decision is that only
   * whole rules have an enable/disable toggle, so the panel's per-level switch
   * was removed. Kept on the draft because `toPayload` has to name it — see the
   * note there — and because the panel says so out loud on a level that is off.
   */
  enabled: boolean;
  approverIds: string[];
  /**
   * Whether one sign-off settles this level (`any`) or it needs as many distinct
   * sign-offs as it has primary approvers (`all`). A field of its own rather
   * than a `passthrough` key, for the reason `conditionLogic` gives: a field
   * written by the panel must not also arrive by way of the passthrough spread.
   *
   * Escalation lets backups count toward that number and never lowers it
   * (`Approvals::Step#approval_satisfied?`). The copy around the control names
   * the choice and does not describe the escalated case; whether it does is a
   * copy decision.
   */
  approverLogic: ApproverLogic;
  /**
   * User approvers the API returned without a `user_id`, i.e. whose user has
   * since been offboarded. They cannot be round-tripped and are dropped on save
   * — see `draftFor`. Counted so the panel can say so out loud rather than
   * quietly shrinking the level.
   *
   * Counts **primary and backup** rows. A dropped backup is the same loss of
   * coverage as a dropped primary, and an offboarded row has no `user_id` to
   * select in either picker — so it cannot appear in the backup roster the panel
   * now renders any more than it could before, and leaving it out would make the
   * warning silent for the one case the admin has no other way to see.
   */
  offboardedApproverCount: number;
  /** The data-key conditions this level is gated on (AXHE-3929). */
  conditions: ConditionDraft[];
  /**
   * How they combine — the And/Or connector shown between rows (AXHE-3930).
   *
   * Lifted out of `passthrough` now that the builder renders it. Round-tripping
   * it there was correct while nothing showed it, but a field that is both
   * spread from passthrough and written by the panel would have the stale copy
   * win on save.
   */
  conditionLogic: ConditionLogic;
  /** Condition types this builder does not render, carried through untouched. */
  conditionPassthrough: StoredCondition[];
  /**
   * The level's conditions exactly as the API returned them.
   *
   * Kept so a save with no catalog re-emits what was there rather than a set
   * rebuilt by a builder that could not read it. Without the catalog the builder
   * is not rendered and these rows cannot have been edited, so replaying them is
   * the definition of leaving them alone — not a coincidence of
   * `toStoredConditions` happening to round-trip.
   */
  storedConditions: StoredCondition[];
  /**
   * The level's escalation, or `null` when it has none (AXHE-3932).
   *
   * Its three columns are lifted out of `passthrough` for the reason
   * `conditionLogic` gives: a field written by the panel must not *also* arrive
   * by way of the passthrough spread, or the stale copy wins on save. Here that
   * would be worse than stale — removing the block emits no timer, so a
   * passthrough copy would put back the deadline the admin just deleted.
   */
  backup: BackupDraft | null;
  /** Round-tripped, never edited here. */
  passthrough: Omit<
    ApprovalRuleLevel,
    | 'name'
    | 'enabled'
    | 'approvers'
    | 'position'
    | 'conditions'
    | 'condition_logic'
    | 'approver_logic'
    | 'backup_approvers'
    | 'escalate_after'
    | 'escalate_unit'
  >;
}

/**
 * What a rule's scope says, as the panel's radio pair puts it (AXHE-4467).
 *
 * The two states the stored scope actually has. `all` covers both ways "applies
 * to everything" is written — no conditions at all, and a lone `always` row.
 */
export type ScopeChoice = 'all' | 'conditions';

/** Keys minted for scope condition rows, kept clear of any level's. */
export const SCOPE_CONDITION_PREFIX = 'scope';

export interface RuleDraft {
  name: string;
  subjectType: SubjectType;
  /**
   * Carried, never edited here (AXHE-4470). A rule's one enable/disable toggle
   * lives in its list row, which PATCHes `enabled` on its own; the panel's
   * duplicate switch was removed. Round-tripped so saving an edit does not
   * disarm — or arm — a gate the admin came here to rename.
   */
  enabled: boolean;
  levels: LevelDraft[];
  /**
   * Whether the rule gates everything or only what its conditions select.
   *
   * Held as its own field rather than derived from `scopeConditions.length`,
   * because the conditional branch has to be selectable *before* it has a
   * condition in it — the mock opens that branch with an empty row waiting to be
   * filled, and a derived flag would snap straight back to `all`.
   */
  scopeChoice: ScopeChoice;
  /** The rule's data-key scope conditions, edited here (AXHE-4467). */
  scopeConditions: ConditionDraft[];
  /** Scope condition types this builder does not render, carried untouched. */
  scopeConditionPassthrough: StoredCondition[];
  /**
   * The scope exactly as the API returned it.
   *
   * Two jobs. It is what a save with no field catalog re-emits, for the reason
   * `LevelDraft#storedConditions` gives. And it is where the `always` rows are
   * read back from on the way out, so choosing "All offers" keeps whichever of
   * the two encodings the rule already had rather than rewriting it.
   */
  storedScopeConditions: StoredCondition[];
  scopeConditionLogic: ConditionLogic;
}

export const emptyLevel = (key: string): LevelDraft => ({
  key,
  name: '',
  // The only value a level added here can have. Nothing can switch it off, and
  // `SaveRule#create_level` defaults an absent `enabled` to true anyway — naming
  // it keeps the payload honest about a field the panel still sends.
  enabled: true,
  approverIds: [],
  // Matches the column default, and is the reading an admin who never opens the
  // control ends up with: one approval settles the level.
  approverLogic: 'any',
  offboardedApproverCount: 0,
  conditions: [],
  // Matches the column default. A level whose conditions all have to hold is the
  // stricter reading, and the one an admin who has not touched the connector
  // gets either way.
  conditionLogic: 'and',
  conditionPassthrough: [],
  storedConditions: [],
  // A new level escalates to nobody until an admin says otherwise. Escalation is
  // an override on the level's own routing, and a level that quietly reassigns
  // itself is the wrong direction to fail.
  backup: null,
  passthrough: {},
});

/**
 * A backup block as adding one opens it.
 *
 * Opens with the timer already filled rather than blank, which is how the mock
 * opens it: the block only exists to say "after this long", so an empty number
 * beside an empty roster reads as two unanswered questions instead of one.
 */
export const emptyBackup = (): BackupDraft => ({
  approverIds: [],
  escalateAfter: DEFAULT_ESCALATE_AFTER,
  escalateUnit: DEFAULT_ESCALATE_UNIT,
  // Nothing stored behind a block added here, so the offered set is the design's
  // two and nothing else.
  storedUnit: DEFAULT_ESCALATE_UNIT,
  roleApprovers: [],
});

/**
 * The timer as the API wants it, or `undefined` when the field does not hold a
 * number the server would accept.
 *
 * Integers only, and above zero: `RuleLevel` validates
 * `numericality: { only_integer: true, greater_than: 0 }`, so "2.5" and "0" are
 * rows the save would 422 on. Not bounded by the range the input steps through —
 * see `ESCALATE_AFTER_MAX` — but bounded by what the column holds, see
 * `ESCALATE_AFTER_STORABLE_MAX`.
 */
export const escalateAfterValue = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;

  const value = Number(trimmed);
  if (value < ESCALATE_AFTER_MIN) return undefined;

  return value <= ESCALATE_AFTER_STORABLE_MAX ? value : undefined;
};

/**
 * How many approvers the block would escalate to, visible rows and carried role
 * rows together.
 *
 * The role rows count even though the panel cannot show them: they are what the
 * engine escalates to, so a block holding nothing but a role backup is
 * configured, not empty, and telling the admin otherwise would be false.
 */
export const backupApproverCount = (backup: BackupDraft): number =>
  backup.approverIds.length + backup.roleApprovers.length;

/**
 * How many approvers a level has, named and by role.
 *
 * Both count: a role approver is one the picker cannot show and the runtime
 * resolves all the same, so a level staffed only by roles is staffed. Exported
 * because the card's Any/All control is offered from two upwards, and it has to
 * count the same set `validate` does.
 */
export const approverCount = (level: LevelDraft): number =>
  level.approverIds.length +
  ((level.passthrough as { roleApprovers?: ApproverRows }).roleApprovers ?? [])
    .length;

export const draftForNew = (subjectType: SubjectType): RuleDraft => ({
  name: '',
  subjectType,
  // A new rule starts active: an admin who just wrote a gate meant it to gate.
  // Sent explicitly rather than left out — `SaveRule#rule_attributes` defaults a
  // *create* to `enabled: false`, "because a governance gate that switches
  // itself on is the wrong direction to fail", so omission would file every new
  // rule inert with nothing on screen saying so.
  enabled: true,
  levels: [emptyLevel('level-0')],
  // The mock's default, and the wider net of the two: a rule that gates
  // everything is the one an admin who skips this choice ends up with, and it
  // fails toward asking for approval rather than away from it.
  scopeChoice: 'all',
  scopeConditions: [],
  scopeConditionPassthrough: [],
  storedScopeConditions: [],
  scopeConditionLogic: 'and',
});

/**
 * The scope rows this panel is not showing the admin.
 *
 * Anything that narrows the rule and is not on screen: the condition types the
 * builder does not render (position, location, opening, attribute — AXHE-4383),
 * and, when the catalog never arrived, the data-key rows too, since the builder
 * renders its own error state instead of them.
 *
 * The panel uses this to withhold the "All …" choice. Selecting it means "drop
 * every condition on this rule", and a panel that cannot show a condition must
 * not offer to delete it — the admin would be agreeing to lose scoping they have
 * no way to read.
 *
 * ## A data-key row naming a departed field is NOT hidden
 *
 * Deliberately, and it was counted here for a while. `draftConditions` selects
 * on `type` alone, so such a row is drafted and rendered like any other — with
 * an empty field select and `conditionFieldUnavailable` under it ("This field is
 * no longer available. Choose another."), which is the message AXHE-3929 added
 * for exactly this. Counting it as hidden made the note above it false while the
 * row sat directly beneath, and disabled the "All …" escape that
 * `scopeNeedsCondition` points at — so removing the stale row left the rule
 * unsaveable either way, with its error naming a control that was greyed out.
 *
 * It also protects nothing. `scopeError` refuses the save as `bad_condition`
 * until the row is resolved, so a departed field cannot round-trip through a
 * select with no matching option: the admin repoints it, removes it, or does not
 * save. Hiding it instead would put a broken gate beyond reach of the one panel
 * that can fix it, and the engine already reads an unresolvable scope field as
 * no-match — the rule has silently stopped gating, which is the thing an admin
 * most needs to see.
 *
 * @param catalog the loaded field catalog, or `undefined` when it has not
 *   arrived
 */
export const hiddenScopeConditions = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): StoredCondition[] =>
  catalog
    ? draft.scopeConditionPassthrough
    : draft.storedScopeConditions.filter(isScopingCondition);

/**
 * Whether saving this rule as conditional would silently drop a stored `always`
 * row that sits beside a scoping one.
 *
 * `scopeChoice` is read row by row (`scopeDraftFor`): a scope holding both an
 * `always` row and a scoping row resolves to `conditions`, and `scopeRows` then
 * emits only the scoping rows — `scopePassthroughConditions` holds `always`
 * back. That drop is correct: choosing "conditions" means the rule narrows, and
 * an `always` row makes it not. But under `or` logic the *stored* rule matches
 * everything today — `ConditionEvaluator#all?` short-circuits `any?` on the
 * `always` row — so the drop changes what the rule gates, from all subjects to
 * the ones the scoping rows select. Silent, it is the failure this panel exists
 * to prevent: the display already reads "meet conditions" while the gate is
 * open, and the save quietly makes the display true.
 *
 * Gated on the catalog, because that is what decides whether the drop actually
 * happens. `scopeRows` only rebuilds the scope through `toStoredConditions` —
 * which excludes `always` — when the catalog is present; without it the stored
 * scope is replayed verbatim and the `always` row survives. Saying "saving
 * removes it" without the catalog would be a claim the save does not make.
 *
 * Not reachable from anything the editor writes — `always` beside a scoping row
 * comes from the API or legacy data — so this narrows to a note, not a block.
 * The note names the drop; whether it also *narrows* depends on the logic, which
 * the panel reads from `scopeConditionLogic`.
 */
export const scopeDropsAlways = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): boolean =>
  catalog !== undefined &&
  draft.scopeChoice === 'conditions' &&
  draft.storedScopeConditions.some(row => row.type === ALWAYS) &&
  draft.storedScopeConditions.some(isScopingCondition);

const isRoleApprover = (approver: ApprovalRuleApprover) =>
  approver.approver_type === 'role';

/**
 * A role approver narrowed from the read shape to the write shape.
 *
 * The read type widens `approver_type` to string and makes the role fields
 * nullable; carrying those straight back would not satisfy the write contract.
 */
const roleRow = (approver: ApprovalRuleApprover) => ({
  approver_type: 'role' as const,
  role_name: approver.role_name ?? undefined,
  role_scope: approver.role_scope ?? undefined,
});

/**
 * A *user* approver with no user_id is one whose user has been offboarded:
 * `RuleLevelApprover#user` is scoped to live users, so the serializer emits
 * `user_id: null` for them. They are genuinely unsendable — the write contract
 * requires a user_id and `SaveRule#user_for` resolves it through
 * `account.users`, which excludes deleted users. That matches the engine, which
 * already resolves them to nobody (`ApproverResolver#resolve_user` returns []
 * for a nil user). The defect was only that it happened invisibly, so they are
 * counted and the panel says so.
 */
const offboardedCount = (approvers: ApprovalRuleApprover[] = []) =>
  approvers.filter(approver => !isRoleApprover(approver) && !approver.user_id).length;

/**
 * The scope half of the draft, read out of the stored condition rows.
 *
 * Its own function so `draftFor` stays a single expression: the split needs the
 * stored rows named, and every field below is derived from that one array.
 */
const scopeDraftFor = (rule: ApprovalRule) => {
  const stored = (rule.scope_conditions ?? []) as StoredCondition[];

  return {
    // Read from what the scope *does*, not from a stored flag: there is no
    // column saying which radio was picked, and there does not need to be. A
    // scope that narrows nothing — no rows, or nothing but `always` — is the
    // "All …" case, and every other scope selects.
    scopeChoice: (stored.some(isScopingCondition)
      ? 'conditions'
      : 'all') as ScopeChoice,
    scopeConditions: draftConditions(stored),
    scopeConditionPassthrough: scopePassthroughConditions(stored),
    storedScopeConditions: stored,
    scopeConditionLogic: rule.scope_condition_logic ?? 'and',
  };
};

/**
 * The escalation half of one level's draft, or `null` when it has none.
 *
 * ## What counts as "has one"
 *
 * Any of the three: a backup approver the panel can show, a role backup it
 * cannot, or a timer on its own. The last is the interesting case — a timer with
 * nobody to escalate to is inert (`AdvanceRequest#escalate_at_for` returns nil
 * when a step has no backup approvers), and stored data holds it because
 * `RuleLevel#escalation_settings_are_complete` only refuses a *half*-filled
 * timer, not a complete one with an empty roster.
 *
 * Opening the block on it is the point. Left closed, the timer would sit in the
 * level doing nothing with nothing on screen naming it, and the admin's only
 * route to the state would be to add a backup and discover a deadline they never
 * set. Open, both exits are in the panel: staff it, or remove the block.
 */
/**
 * The unit to draft for a stored timer — see `backupDraftFor` for why the
 * number decides whether a missing one is defaulted or kept blank.
 */
const unitFor = (
  escalateAfter?: number | null,
  escalateUnit?: string | null,
): string => escalateUnit ?? (escalateAfter == null ? DEFAULT_ESCALATE_UNIT : '');

const backupDraftFor = ({
  backups,
  escalateAfter,
  escalateUnit,
}: {
  backups?: ApprovalRuleApprover[] | null;
  escalateAfter?: number | null;
  escalateUnit?: string | null;
}): BackupDraft | null => {
  const rows = backups ?? [];
  const hasTimer = escalateAfter != null || escalateUnit != null;
  if (rows.length === 0 && !hasTimer) return null;

  return {
    // Role backups have no `user_id`, and an offboarded user's is null — the same
    // two exclusions the primary picker makes, for the same two reasons. The
    // second one is not cosmetic: sent back as
    // `{ approver_type: 'user', user_id: null }` the server does not skip the row
    // — `SaveRule#user_for` returns nil for a blank user_id and
    // `RuleLevelApprover#identity_matches_approver_type` then fails it with "User
    // must be set for a user approver", which 422s the *whole* save. Dropping it
    // here is what keeps a rule editable after one of its backups has left.
    approverIds: rows
      .filter(approver => !isRoleApprover(approver) && approver.user_id)
      .map(approver => approver.user_id as string),
    // `?? ''` rather than the default: a level whose unit is set and whose number
    // is not is a half-filled timer the server already refuses, so it has to
    // render as the empty field it is and be named by validation. Substituting a
    // 2 here would put a deadline on the level that nobody chose.
    escalateAfter: escalateAfter?.toString() ?? '',
    // Defaulted only when the number is missing too.
    //
    // The two columns are a pair — `RuleLevel#escalation_settings_are_complete`
    // refuses one without the other — so "no unit" means two different things
    // depending on the number beside it, and completing both alike would invent
    // configuration in one of them:
    //
    //   *neither set* is a level with no timer, and Days is just what the picker
    //   opens on. Nothing is emitted without a number, so the choice costs
    //   nothing and a blank select would only read as a field to fix.
    //   *a number with no unit* is a half-pair the engine treats as no timer at
    //   all (`escalate_at_for` returns nil on a blank unit, so `escalate_at` is
    //   never stamped and the backups are never asked). Supplying Days here
    //   turns that dead timer into a live deadline on the next save of a rule
    //   somebody only renamed — arming an escalation nobody chose, which is the
    //   direction `SaveRule` already says this project exists to fail away from.
    //
    // So the half-pair keeps its blank and `validate` names it instead.
    escalateUnit: unitFor(escalateAfter, escalateUnit),
    storedUnit: unitFor(escalateAfter, escalateUnit),
    roleApprovers: rows.filter(isRoleApprover).map(roleRow),
  };
};

/**
 * The units to offer for a stored timer.
 *
 * The design's two, plus whatever the level already holds when that is neither.
 * The engine's enum has a third (`business_days`) that this picker deliberately
 * does not offer, and the same shape would arrive from a server that added a
 * fourth — either way, coercing it to Days would silently move the deadline on
 * the next save of a rule the admin only renamed. Listing the stored token is
 * how the condition builder keeps a value it does not recognise round-trippable,
 * and it leaves both exits open: keep it, or pick one of the two.
 */
export const escalateUnitOptions = (stored: string): string[] =>
  !stored || (ESCALATE_UNITS as readonly string[]).includes(stored)
    ? [...ESCALATE_UNITS]
    : [...ESCALATE_UNITS, stored];

export const draftFor = (rule: ApprovalRule): RuleDraft => ({
  name: rule.name ?? '',
  subjectType: rule.subject_type ?? 'OfferLetter',
  enabled: rule.enabled ?? true,
  ...scopeDraftFor(rule),
  levels: (rule.levels ?? []).map((level, index) => {
    // Everything the panel owns is pulled out of the passthrough rest, so that
    // no field arrives twice — once from the draft and once from the spread,
    // where the stale copy would win on save.
    //
    // `conditions` and `condition_logic` are here because this builder edits
    // them: spread back verbatim they would land on top of the edited set.
    //
    // The three escalation keys are here because AXHE-3932 made the block
    // editable. Before that they were round-tripped, `backup_approvers` through
    // the same filter and narrowing the primary rows go through — which is what
    // kept a rule with an offboarded backup savable from a panel that could not
    // show the row. That filter still runs, in `backupDraftFor`.
    const {
      name,
      enabled,
      approvers,
      approver_logic: approverLogic,
      backup_approvers: backups,
      escalate_after: escalateAfter,
      escalate_unit: escalateUnit,
      position,
      conditions,
      condition_logic: conditionLogic,
      ...passthrough
    } = level;
    const stored = (conditions ?? []) as StoredCondition[];

    return {
      // Keyed by array index rather than `position`. Both survive a reorder
      // equally — a reorder rewrites the array and the keys are assigned once
      // per draft — but `position ?? index` can collide if one level carries a
      // position and its neighbour does not (level 0 with a null position and
      // level 1 with position 0 both become `level-0`), and two levels sharing a
      // key would share a validation message and a form field id.
      key: `level-${index}`,
      conditions: draftConditions(stored),
      conditionLogic: conditionLogic ?? 'and',
      conditionPassthrough: passthroughConditions(stored),
      storedConditions: stored,
      name: name ?? '',
      enabled: enabled ?? true,
      // Role approvers have no user_id. They are round-tripped through
      // `passthrough` rather than being dropped, so editing a rule that names a
      // role does not quietly delete that role from the level.
      approverIds: (approvers ?? [])
        .filter(approver => !isRoleApprover(approver) && approver.user_id)
        .map(approver => approver.user_id as string),
      approverLogic: approverLogic ?? 'any',
      offboardedApproverCount: offboardedCount(approvers) + offboardedCount(backups),
      backup: backupDraftFor({
        backups,
        escalateAfter,
        escalateUnit,
      }),
      passthrough: {
        ...passthrough,
        // Narrowed to the *write* shape here rather than at submit, so the draft
        // holds only things it can actually send.
        roleApprovers: (approvers ?? []).filter(isRoleApprover).map(roleRow),
      } as LevelDraft['passthrough'],
    };
  }),
});

/**
 * The rule's scope switched to the other choice.
 *
 * Opening the conditional branch seeds a row when there is nothing to show,
 * which is how the mock opens it — a branch selected and visibly empty reads as
 * "this rule has no conditions" rather than as an invitation to write one. Only
 * when there is nothing at all: a rule already scoped by a type this builder does
 * not render has conditions, and an empty data-key row would be a field the admin
 * never asked for and now has to fill.
 *
 * The drafted rows are kept when switching back to "All …" rather than cleared.
 * `toPayload` does not emit them in that state, so nothing reaches the server —
 * and an admin who picks the wrong radio does not lose what they had typed.
 */
export const withScopeChoice = (
  draft: RuleDraft,
  scopeChoice: ScopeChoice,
  catalog?: FieldCatalog,
): RuleDraft => {
  // The type a seeded row starts on is the server's answer, not a constant here:
  // an opening rule cannot be scoped by a custom attribute at all, so seeding one
  // would open the branch on a control the save refuses (AXHE-4383). No catalog
  // means no seed — the builder is showing its error state instead of rows, and
  // `scopeError` already refuses the save.
  const seedType =
    catalog &&
    defaultConditionType(catalog, draft.subjectType, 'scope', draft.scopeConditions);
  const needsSeedRow =
    scopeChoice === 'conditions' &&
    seedType !== undefined &&
    draft.scopeConditions.length === 0 &&
    draft.scopeConditionPassthrough.length === 0;

  return {
    ...draft,
    scopeChoice,
    scopeConditions: needsSeedRow
      ? [
          emptyConditionOfType(
            mintConditionKey(SCOPE_CONDITION_PREFIX, draft.scopeConditions),
            seedType,
          ),
        ]
      : draft.scopeConditions,
  };
};

/**
 * The levels with the one at `from` moved to `to`.
 *
 * Array order *is* the level order: `toPayload` deliberately omits `position`
 * and lets the server number the levels from the sequence it is sent, so moving
 * an entry here is the whole edit — there is no position field left behind to
 * fall out of step with what the panel is showing.
 *
 * A copy rather than a splice in place, because the draft is React state.
 */
export const reorderLevels = (
  levels: LevelDraft[],
  from: number,
  to: number,
): LevelDraft[] => {
  const reordered = [...levels];
  const [moved] = reordered.splice(from, 1);
  reordered.splice(to, 0, moved);
  return reordered;
};

/**
 * The two ways a backup block's wait is wrong, kept apart because one message
 * cannot be true of both.
 *
 * `bad_escalation_timer` is a field that **holds something** the server would not
 * take: a fraction, or a figure past what the column stores. Refused whether or
 * not the block is staffed, and that is the difference that matters — judged on
 * the roster alone, an unusable number typed on an unstaffed block was dropped by
 * `escalationRows` and the level saved with both columns cleared, having visibly
 * held a figure the whole time. That is the silent shrink this panel's offboarded
 * warning exists to prevent, arriving by another door.
 *
 * `no_escalation_timer` is a staffed backup block whose wait is **empty**.
 *
 * Refused rather than warned about, because the two halves of the block mean
 * nothing apart: backups are only ever asked when a deadline passes, so a roster
 * with no deadline is a set of people the engine will never contact
 * (`escalate_at_for` returns nil and `Step.due_for_escalation` never matches the
 * step). The admin has just staffed them, so saying nothing would confirm a
 * routing that does not exist.
 *
 * `no_backup_approvers` is the mirror case: a block whose roster is empty. It is
 * a refusal, not a warning, because the two halves mean nothing apart in this
 * direction either — `AdvanceRequest#escalate_at_for` returns nil on a step with
 * no backup approvers, so the wait is never stamped and the block is a deadline
 * the engine does not keep. A card that states one is telling the admin
 * something untrue, which is the state a save must not confirm. Design
 * Requirements §3 refuses it with the rest: "Submitting with an empty rule name,
 * a level with no approver selected, or a backup-approver block with no backup
 * approver selected reveals an inline error on the exact field".
 *
 * A block staffed only by a role backup is staffed — `backupApproverCount`
 * counts what the panel cannot show, because the engine escalates to it.
 *
 * Reachable from stored data as well as from an edit here (`backupDraftFor`
 * opens the block on a timer alone), and that is the case the refusal has to
 * leave a way out of. Removing the block is that way out: it is offered on every
 * empty roster, since a block the remove control is withheld from carries a role
 * backup and is therefore not empty.
 */
export type LevelError =
  | 'no_approvers'
  | 'bad_condition'
  | 'no_backup_approvers'
  | 'no_escalation_timer'
  | 'no_escalation_unit'
  | 'bad_escalation_timer';

/**
 * What is wrong with the rule's scope, when anything is.
 *
 * `no_conditions` is a rule set to apply only to what meets conditions, with
 * none — which the engine reads as applying to *everything* (`all?` is true for
 * an empty set), the opposite of what the panel is showing. Left to save it
 * would gate every offer under a rule that says it gates some.
 *
 * `bad_condition` needs no message of its own: the rows carry their own, the
 * same way a level's do.
 *
 * The two cannot both hold — a scope with no rows has no bad ones — so this is
 * one value rather than the list a level's errors need.
 */
export type ScopeError = 'no_conditions' | 'bad_condition';

export interface ValidationErrors {
  name?: 'required';
  scope?: ScopeError;
  /**
   * Keyed by level key, so the message lands on the level that is wrong, and
   * carrying **every** problem on that level rather than the first.
   *
   * A level with no approvers and a half-written condition has two things wrong
   * with it. Reporting one, then the other after the admin fixes it and saves
   * again, is a round trip per mistake — the same argument `ValidatePlacements`
   * already states for a form that submits its whole set in one request.
   */
  levels: Record<string, LevelError[]>;
}

/**
 * The rule's scope as the API wants it (AXHE-4467).
 *
 * ## "All …" keeps the encoding the rule already had
 *
 * Applying to everything is stored two ways — no rows at all, and a lone
 * `always` row — and `ConditionEvaluator#all?` answers both the same. So this
 * re-emits whichever the rule arrived with rather than normalising: a rule
 * written as `always` by the design's own trigger vocabulary keeps saying
 * `always` after an edit that only renamed it. What it does drop is every row
 * that *narrows* the rule, which is what choosing "All …" means. The panel
 * withholds that choice when it cannot show those rows — see
 * `hiddenScopeConditions`.
 *
 * ## The conditional branch drops `always`
 *
 * A scope of `[always, data_key…]` under `or` logic matches everything, so a
 * rule reading "Offers that meet conditions" would gate every offer. Under `and`
 * the row is merely redundant. Either way it contradicts the choice the admin
 * just made, so it goes.
 */
const scopeRows = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): StoredCondition[] => {
  if (draft.scopeChoice === 'all') {
    return draft.storedScopeConditions.filter(row => !isScopingCondition(row));
  }

  // Without the catalog the scope builder rendered its own error state instead
  // of these rows, so they cannot have been edited and are replayed as they came
  // — the same reason a level's are.
  return catalog
    ? toStoredConditions(
        draft.scopeConditions,
        draft.scopeConditionPassthrough,
        catalog,
      )
    : draft.storedScopeConditions;
};

/**
 * @param catalog as for `validate` — without it a scope condition cannot be
 *   judged, but whether the rule would send any at all still can be
 */
const scopeError = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): ScopeError | undefined => {
  if (draft.scopeChoice !== 'conditions') return undefined;

  // Judged on the rows this draft would actually *send*, not on the drafts the
  // panel is holding. The two come apart when the catalog never arrived:
  // selecting this branch seeds a draft row, but `scopeRows` re-emits the stored
  // scope rather than the drafts — so a new rule saved that way sent `[]`, which
  // `ConditionEvaluator#all?` reads as *everything*. A rule that says it gates
  // some offers and gates all of them is the wrong direction to fail, and it was
  // reachable because the panel withheld only the other radio.
  //
  // Asking the payload also gets the case the old count-based check needed a
  // second clause for: a rule scoped only by a condition type this builder does
  // not render still sends that row, so it is scoped and saves — refusing it
  // would strand every rule authored before AXHE-4383 lands the rest of the
  // catalog.
  if (!scopeRows(draft, catalog).some(isScopingCondition)) return 'no_conditions';

  // Judged at `scope`, which is stricter than a level: a pay condition and an
  // unsourced attribute are both refused here and accepted there, so passing the
  // wrong one would let the panel save a rule the server rejects.
  if (
    catalog &&
    conditionSetErrors(draft.scopeConditions, catalog, {
      subjectType: draft.subjectType,
      scope: 'scope',
    })
  ) {
    return 'bad_condition';
  }

  return undefined;
};

/**
 * @param catalog the loaded field catalog, or `undefined` when it has not
 *   arrived. Without it the condition builder is not rendered, so a condition
 *   cannot have been edited and there is nothing here to judge it against —
 *   validating anyway would flag every stored condition as unknown and block a
 *   save that only touched approvers.
 */
export const validate = (
  draft: RuleDraft,
  catalog?: FieldCatalog,
): ValidationErrors => {
  const levels: ValidationErrors['levels'] = {};

  draft.levels.forEach(level => {
    const problems: LevelError[] = [];

    // A level is staffed by named users OR by roles resolved at submission. Only
    // counting users would report a role-only level as empty and refuse to save
    // a rule the server considers perfectly valid.
    if (approverCount(level) === 0) {
      problems.push('no_approvers');
    }

    // A half-written condition would be saved as one the engine cannot
    // evaluate, which holds every offer for a human rather than gating the ones
    // the admin meant.
    if (
      catalog &&
      conditionSetErrors(level.conditions, catalog, {
        subjectType: draft.subjectType,
        scope: 'level',
      })
    ) {
      problems.push('bad_condition');
    }

    // A block with nobody on it. Judged before the wait so a level with neither
    // half answered is named on the roster, which is the half the admin came
    // here to fill — and the exit the message offers, removing the block, clears
    // the wait with it.
    if (level.backup && backupApproverCount(level.backup) === 0) {
      problems.push('no_backup_approvers');
    }

    // A wait with no unit. Judged apart from the number so the message can name
    // the half that is missing, and refused rather than warned about: the pair
    // is what the server validates together, so a save carrying one of them
    // 422s the whole rule with a message the panel cannot place on a field.
    //
    // Not reachable from stored data today — the same model validation makes the
    // half-pair unwritable — so this guards the panel against being the thing
    // that completes one, and against a server that ever relaxes the rule.
    if (
      level.backup &&
      level.backup.escalateUnit.trim() === '' &&
      escalateAfterValue(level.backup.escalateAfter) !== undefined
    ) {
      problems.push('no_escalation_unit');
    }

    if (level.backup && escalateAfterValue(level.backup.escalateAfter) === undefined) {
      // Empty is judged on the roster, so a block with neither half answered
      // carries one message rather than two. Anything else is judged on its own,
      // because a figure the save would silently drop has to be named whether or
      // not somebody is standing behind it.
      if (level.backup.escalateAfter.trim() === '') {
        if (backupApproverCount(level.backup) > 0) problems.push('no_escalation_timer');
      } else {
        problems.push('bad_escalation_timer');
      }
    }

    if (problems.length > 0) levels[level.key] = problems;
  });

  return {
    name: draft.name.trim() ? undefined : 'required',
    scope: scopeError(draft, catalog),
    levels,
  };
};

/** Whether a level was flagged for this specific problem. */
export const levelHasError = (
  errors: ValidationErrors,
  levelKey: string,
  error: LevelError,
) => (errors.levels[levelKey] ?? []).includes(error);

export const hasErrors = (errors: ValidationErrors) =>
  errors.name !== undefined ||
  errors.scope !== undefined ||
  Object.keys(errors.levels).length > 0;

/**
 * The draft as the API wants it.
 *
 * ## Why every level carries its conditions back
 *
 * PATCH replaces the level tree wholesale — "Sent, it replaces the whole tree —
 * a level absent from the array is deleted" — and the controller reads each
 * level's conditions straight out of the submitted document
 * (`condition_rows(raw_level[:conditions])`), where a missing key becomes `[]`.
 *
 * So a panel that submitted levels without their conditions would not leave them
 * alone: it would silently **clear** them, turning a rule that gates offers over
 * $100k into one that gates every offer. Round-tripping them is what makes it
 * safe for this panel to edit names and approvers without owning the condition
 * builder (AXHE-4107).
 *
 * The same argument holds for the scope condition types this panel does not
 * render — see `scopeRows`.
 *
 * @param catalog the loaded field catalog, or `undefined`. Without it the
 *   builder never rendered, so each level's conditions are re-emitted exactly as
 *   the API returned them rather than rebuilt from drafts read without the
 *   vocabulary needed to interpret them.
 */
/**
 * One level's escalation, as the API wants it.
 *
 * Two separate emissions rather than one object, because each clears
 * differently and `SaveRule` recreates every level from the submitted document —
 * so a key this omits is a column the saved level does not have.
 *
 * - **The timer pair goes together or not at all.** `RuleLevel`'s
 *   `escalation_settings_are_complete` refuses a unit with no number and a
 *   number with no unit, so emitting the unit beside an unusable number would
 *   422 the whole save. The guard below is the second lock on that pair, after
 *   the one `validate` puts on the form.
 *
 *   It is not a second lock on an **unstaffed** block, and deliberately not:
 *   `validate` refuses that on the form and nothing here re-checks the roster
 *   before emitting the timer. Adding the check would be a branch no call site
 *   can reach, since `handleSave` runs `validate` first and returns.
 * - **`backup_approvers` is emitted only when it has a row**, so a level with no
 *   backups submits without the key rather than with an empty array — the shape
 *   a level the API never described backups for has always had.
 */
const escalationRows = (backup: BackupDraft | null) => {
  if (!backup) return {};

  const after = escalateAfterValue(backup.escalateAfter);
  const unit = backup.escalateUnit.trim();
  const rows: ApproverRows = [
    ...backup.approverIds.map(userId => ({
      approver_type: 'user' as const,
      user_id: userId,
    })),
    ...backup.roleApprovers,
  ];

  return {
    // Both halves or neither, never one. `RuleLevel` refuses a number without a
    // unit and a unit without a number, so sending one 422s the whole rule with
    // a message the panel cannot place on a field — and a level whose stored
    // unit is missing has to come back out the way it went in rather than
    // gaining a deadline. `validate` refuses that state before this runs; this
    // is the second lock, on the payload rather than on the form.
    ...(after !== undefined &&
      unit !== '' && {
        escalate_after: after,
        // Cast because the OpenAPI document types this narrower than the server
        // does — its enum is hours/days while `RuleLevel` enumerizes a third
        // value — and `escalateUnitOptions` exists so a stored token outside the
        // enum survives an edit rather than being rewritten to Days.
        escalate_unit: unit as EscalateUnit,
      }),
    ...(rows.length > 0 && { backup_approvers: rows }),
  };
};

export const toPayload = (draft: RuleDraft, catalog?: FieldCatalog): RulePayload => ({
  rule: {
    name: draft.name.trim(),
    subject_type: draft.subjectType,
    enabled: draft.enabled,
    scope_conditions: scopeRows(draft, catalog),
    scope_condition_logic: draft.scopeConditionLogic,
    levels: draft.levels.map(level => {
      const { roleApprovers = [], ...otherFields } =
        level.passthrough as { roleApprovers?: ApproverRows };

      return {
        ...otherFields,
        // Edited rows plus the condition types this builder does not render, so
        // editing a data-key condition cannot delete a rule's position or
        // location scoping along with it.
        conditions: catalog
          ? toStoredConditions(level.conditions, level.conditionPassthrough, catalog)
          : level.storedConditions,
        // Sent unconditionally, unlike `conditions`. It is read straight off the
        // level rather than rebuilt from the catalog, so the draft holds the
        // stored value even when the builder never rendered — and `SaveRule`
        // defaults a missing key to 'and', which would otherwise flip an OR
        // level back to AND on a save that only touched approvers.
        condition_logic: level.conditionLogic,
        // `undefined`, not `null`: a level's name is nullable when read back but
        // the write contract only accepts a string or its absence, and an
        // unnamed level is one the payload simply does not name.
        name: level.name.trim() || undefined,
        // Re-emitted, not decided (AXHE-4470). The panel no longer offers a
        // level toggle, but PATCH replaces the level tree wholesale and
        // `SaveRule#create_level` defaults an absent `enabled` to **true** — so
        // dropping this key would not be inert, it would silently re-activate a
        // level an admin had switched off and put approvals back through an
        // approver they meant to bypass. Carrying the value forward is what
        // makes removing the switch a UI change rather than a data migration.
        enabled: level.enabled,
        // Sent unconditionally, for the reason `condition_logic` above gives:
        // `SaveRule#create_level` defaults a missing key to 'any', so a save
        // that only touched a rule's name would quietly turn an all-of level
        // into an any-of one.
        approver_logic: level.approverLogic,
        // The backup roster and its timer (AXHE-3932). Emitted from the draft
        // and never from `passthrough` — see `LevelDraft#backup` for why those
        // three keys are destructured out of it.
        ...escalationRows(level.backup),
        // Position is deliberately absent: the server assigns it from this
        // array's order, so the client cannot desync the numbering from the
        // sequence it is displaying.
        approvers: [
          ...level.approverIds.map(userId => ({
            approver_type: 'user' as const,
            user_id: userId,
          })),
          ...roleApprovers,
        ],
      };
    }),
  },
});
